import { useState } from "react";
import { LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_DESTINATION, DEFAULT_ORIGIN } from "@/lib/locations";
import { LocationSearch } from "@/components/LocationSearch";
import { btnGhost, btnPrimary } from "@/components/TopBar";

export const AssignTripDialog = ({ vehicles, onClose, onCreated }) => {
  const [selected, setSelected] = useState(vehicles[0] ? [vehicles[0].id] : []);
  const [origin, setOrigin] = useState(DEFAULT_ORIGIN);
  const [current, setCurrent] = useState(null);
  const [destination, setDestination] = useState(DEFAULT_DESTINATION);
  const [busy, setBusy] = useState(false);

  const allSelected = selected.length === vehicles.length && vehicles.length > 0;

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return toast.error("Select at least one truck");
    if (!origin || !destination) return toast.error("Set a start point and a destination");
    setBusy(true);
    try {
      const { data } = await api.post("/trips/bulk", {
        vehicle_ids: selected,
        origin_name: origin.name,
        origin_pos: { lat: origin.lat, lng: origin.lng },
        destination_name: destination.name,
        destination_pos: { lat: destination.lat, lng: destination.lng },
        current_name: current?.name || null,
        current_pos: current ? { lat: current.lat, lng: current.lng } : null,
      });
      toast.success(`${data.created} trip(s) assigned · ${data.planned_distance_km} km each`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1500] flex items-start justify-center overflow-y-auto p-4 py-10" data-testid="assign-trip-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          type="button"
          data-testid="close-assign-dialog-btn"
          onClick={onClose}
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">dispatch</p>
        <h2 className="font-head text-2xl font-black tracking-tight">ASSIGN TRIP</h2>
        <p className="mt-1 text-[11px] text-[#A1A1AA]">
          Pick one truck or the whole fleet - they all get the same route.
        </p>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label className="overline">trucks · {selected.length} selected</label>
            <button
              type="button"
              data-testid="select-all-vehicles-btn"
              onClick={() => setSelected(allSelected ? [] : vehicles.map((v) => v.id))}
              className={btnGhost}
            >
              {allSelected ? "CLEAR ALL" : "SELECT ALL"}
            </button>
          </div>
          <div
            className="doot-scroll mt-2 max-h-40 overflow-y-auto border border-white/15 bg-[#121212]"
            data-testid="assign-vehicle-list"
          >
            {vehicles.map((v) => (
              <label
                key={v.id}
                data-testid={`assign-vehicle-option-${v.plate_number}`}
                className="flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-2 text-[11px] hover:bg-[#1A1A1A]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(v.id)}
                  onChange={() => toggle(v.id)}
                  className="h-3 w-3 accent-[#00F0FF]"
                />
                <span className="font-head font-bold">{v.plate_number}</span>
                <span className="ml-auto text-[10px] text-[#52525B]">
                  {v.current_location_name || "-"}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-5">
          <LocationSearch label="start from (default Vizag)" value={origin} onChange={setOrigin} testid="assign-origin" />
          <LocationSearch
            label="current location (optional)"
            value={current}
            onChange={setCurrent}
            testid="assign-current"
          />
          <LocationSearch
            label="destination (default Belpara, Balangir)"
            value={destination}
            onChange={setDestination}
            testid="assign-destination"
          />
        </div>

        <button type="submit" disabled={busy} data-testid="submit-assign-trip-btn" className={`${btnPrimary} mt-7 w-full py-3`}>
          {busy && <LoaderCircle size={14} className="animate-spin" />}
          {busy ? "calculating route" : `dispatch ${selected.length || ""}`.trim()}
        </button>
      </form>
    </div>
  );
};
